import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";
import { EMPTY, validateStep } from "@/components/enquiry/schema";
import { StepAbout, StepProject, StepBrief } from "@/components/enquiry/Steps";
import { ErrorState, Spinner, Loading } from "@/components/site/Primitives";

const DRAFT_KEY = "editbay_enquiry_draft";
const STEPS = ["About you", "The project", "The brief"];

export default function Enquire() {
  const { services, site, loading } = useSite();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const startedAt = useRef(Date.now());
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(() => {
    try { return { ...EMPTY, ...JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}"), consent: false }; } catch { return EMPTY; }
  });
  useSeo("enquire", "/enquire");
  const available = useMemo(() => services.filter((s) => s.available), [services]);

  useEffect(() => {
    const slug = params.get("service");
    const match = available.find((s) => s.slug === slug);
    if (match) setForm((f) => ({ ...f, service_id: match.id }));
  }, [params, available]);

  useEffect(() => {
    const { consent, website, ...draft } = form;
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [form]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const next = () => {
    const e = validateStep(step, form);
    setErrors(e);
    if (Object.keys(e).length === 0) setStep(step + 1);
  };

  const submit = async () => {
    const e = validateStep(2, form);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = { ...form, video_count: Number(form.video_count), delivery_date: form.delivery_date || null, started_at: startedAt.current };
      const { data } = await api.post("/enquiries", payload);
      localStorage.removeItem(DRAFT_KEY);
      nav(`/enquire/confirmation/${data.reference}`, { state: data });
    } catch (err) {
      setSubmitError(`${errMsg(err)} Your answers are saved on this device — you can try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  const props = { form, set, errors };
  return (
    <div className="eb-container py-16 md:py-24 grid lg:grid-cols-12 gap-12" data-testid="enquire-page">
      <aside className="lg:col-span-4">
        <p className="eb-label !text-[#00E5FF] mb-6">Project enquiry</p>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-[-0.04em] leading-[0.95]">Send the <span className="eb-accent">brief.</span></h1>
        <p className="mt-6 text-zinc-400 leading-relaxed">We review every brief and confirm scope, price and timeline before work begins.</p>
        {site?.turnaround?.headline && <p className="mt-6 text-sm text-zinc-500 border-l-2 border-[#0055FF] pl-3">{site.turnaround.headline}</p>}
        <ol className="mt-10 space-y-3" aria-label="Progress">
          {STEPS.map((s, i) => (
            <li key={s} className={`flex items-center gap-3 text-sm ${i === step ? "text-white" : i < step ? "text-zinc-400" : "text-zinc-600"}`} data-testid={`enquiry-step-indicator-${i}`}>
              <span className={`w-8 h-8 flex items-center justify-center border ${i <= step ? "border-[#0055FF] bg-[#0055FF]/10" : "border-[#27272A]"}`}>{i + 1}</span>{s}
            </li>
          ))}
        </ol>
      </aside>
      <div className="lg:col-span-8 bg-[#0a0a0a] border border-[#27272A] p-6 md:p-10">
        {loading ? <Loading rows={1} /> : (
          <form onSubmit={(e) => { e.preventDefault(); step < 2 ? next() : submit(); }} noValidate data-testid="enquiry-form">
            <h2 className="text-2xl font-bold mb-8">{STEPS[step]}</h2>
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
                {step === 0 && <StepAbout {...props} />}
                {step === 1 && <StepProject services={available} {...props} />}
                {step === 2 && <StepBrief {...props} />}
              </motion.div>
            </AnimatePresence>
            {submitError && <div className="mt-8"><ErrorState message={submitError} /></div>}
            <div className="mt-10 flex justify-between gap-4">
              {step > 0 ? <button type="button" onClick={() => setStep(step - 1)} className="eb-btn-ghost" data-testid="enquiry-back-btn"><ArrowLeft size={16} />Back</button> : <span />}
              {step < 2 ? (
                <button type="submit" className="eb-btn-primary" data-testid="enquiry-next-btn">Continue<ArrowRight size={16} /></button>
              ) : (
                <button type="submit" disabled={submitting} className="eb-btn-primary" data-testid="enquiry-submit-btn">{submitting ? <><Spinner />Submitting</> : "Submit enquiry"}</button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
